import { useState, useEffect, useCallback } from 'react';
import { PlatformLayout } from '../components/layout/PlatformLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { RefreshCw, Activity, CheckCircle, XCircle, Clock, SkipForward, Loader2, ChevronLeft, ChevronRight, Wifi, WifiOff } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(d) {
  if (!d) return '-';
  try { return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }); }
  catch { return '-'; }
}

function duration(start, end) {
  if (!start || !end) return '-';
  try {
    const ms = new Date(end) - new Date(start);
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${Math.round(ms / 1000)}s`;
    return `${Math.round(ms / 60000)}min`;
  } catch { return '-'; }
}

function StatusBadge({ status }) {
  const config = {
    pending: { icon: Clock, className: 'bg-amber-600/15 text-amber-700 border-amber-200', label: 'En attente' },
    running: { icon: Loader2, className: 'bg-blue-600/15 text-blue-700 border-blue-200', label: 'En cours' },
    syncing: { icon: Loader2, className: 'bg-blue-600/15 text-blue-700 border-blue-200', label: 'Synchro' },
    completed: { icon: CheckCircle, className: 'bg-emerald-600/15 text-emerald-700 border-emerald-200', label: 'Termine' },
    failed: { icon: XCircle, className: 'bg-red-600/15 text-red-700 border-red-200', label: 'Erreur' },
    skipped: { icon: SkipForward, className: 'bg-slate-600/15 text-slate-700 border-slate-200', label: 'Ignore' },
  };
  const c = config[status] || config.pending;
  const Icon = c.icon;
  return (
    <Badge className={`text-[11px] gap-1 ${c.className}`}>
      <Icon className={`w-3 h-3 ${status === 'running' || status === 'syncing' ? 'animate-spin' : ''}`} />
      {c.label}
    </Badge>
  );
}

function TypeBadge({ type }) {
  const labels = { email_sync: 'Email', ringover_sync: 'Ringover', transcription: 'Transcription' };
  const colors = { email_sync: 'bg-blue-600/15 text-blue-700', ringover_sync: 'bg-emerald-600/15 text-emerald-700', transcription: 'bg-violet-600/15 text-violet-700' };
  return <Badge className={`text-[10px] ${colors[type] || 'bg-slate-100 text-slate-700'}`}>{labels[type] || type}</Badge>;
}

const PAGE_SIZE = 20;

export default function PlatformJobsPage() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/platform/jobs/stats`, { headers: { Authorization: `Bearer ${token}` } });
      setStats(res.data);
    } catch (e) { console.error(e); }
  }, [token]);

  const fetchJobs = useCallback(async (p = 0) => {
    setLoading(true);
    try {
      const params = { skip: p * PAGE_SIZE, limit: PAGE_SIZE };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter !== 'all') params.job_type = typeFilter;
      const res = await axios.get(`${API}/platform/jobs`, { headers: { Authorization: `Bearer ${token}` }, params });
      setJobs(res.data.jobs);
      setTotal(res.data.total);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token, statusFilter, typeFilter]);

  useEffect(() => { fetchStats(); fetchJobs(page); }, [page, fetchStats, fetchJobs]);

  const refresh = () => { fetchStats(); fetchJobs(page); };
  const applyFilters = () => { setPage(0); fetchJobs(0); };

  const statCards = stats ? [
    { label: 'Total', value: stats.total, icon: Activity, color: 'text-slate-500', bg: 'bg-slate-500/10' },
    { label: 'En attente', value: stats.pending, icon: Clock, color: 'text-amber-500', bg: 'bg-amber-500/10' },
    { label: 'En cours', value: stats.running, icon: Loader2, color: 'text-blue-500', bg: 'bg-blue-500/10' },
    { label: 'Termines', value: stats.completed, icon: CheckCircle, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
    { label: 'Erreurs', value: stats.failed, icon: XCircle, color: 'text-red-500', bg: 'bg-red-500/10' },
    { label: 'Ignores', value: stats.skipped, icon: SkipForward, color: 'text-slate-400', bg: 'bg-slate-400/10' },
  ] : [];

  return (
    <PlatformLayout title="Jobs & Synchronisations">
      <div className="space-y-5" data-testid="platform-jobs-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <p className="text-sm text-muted-foreground">Suivi des taches</p>
            {stats && (
              <Badge variant="outline" className={`gap-1.5 text-xs ${stats.worker_status === 'active' ? 'text-emerald-600' : stats.worker_status === 'idle' ? 'text-amber-600' : 'text-red-600'}`}>
                {stats.worker_status === 'active' ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                Worker {stats.worker_status === 'active' ? 'Actif' : stats.worker_status === 'idle' ? 'Inactif' : 'Erreur'}
              </Badge>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={refresh} disabled={loading} data-testid="refresh-jobs">
            <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? 'animate-spin' : ''}`} /> Actualiser
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-3 lg:grid-cols-6 gap-3" data-testid="jobs-stats-grid">
            {statCards.map((s, i) => (
              <Card key={i}>
                <CardContent className="pt-4 pb-3 text-center">
                  <s.icon className={`w-4 h-4 mx-auto mb-1 ${s.color}`} />
                  <p className="text-xl font-bold">{s.value}</p>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{s.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-3 items-end">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Statut</label>
            <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setTimeout(applyFilters, 0); }}>
              <SelectTrigger className="w-[140px] h-9 text-sm" data-testid="jobs-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="pending">En attente</SelectItem>
                <SelectItem value="completed">Termines</SelectItem>
                <SelectItem value="failed">Erreurs</SelectItem>
                <SelectItem value="skipped">Ignores</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Type</label>
            <Select value={typeFilter} onValueChange={v => { setTypeFilter(v); setTimeout(applyFilters, 0); }}>
              <SelectTrigger className="w-[150px] h-9 text-sm" data-testid="jobs-type-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="email_sync">Email</SelectItem>
                <SelectItem value="ringover_sync">Ringover</SelectItem>
                <SelectItem value="transcription">Transcription</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Type</TableHead>
                  <TableHead className="text-xs">Tenant</TableHead>
                  <TableHead className="text-xs">Statut</TableHead>
                  <TableHead className="text-xs">Debut</TableHead>
                  <TableHead className="text-xs">Duree</TableHead>
                  <TableHead className="text-xs text-center">Traites</TableHead>
                  <TableHead className="text-xs text-center">Crees</TableHead>
                  <TableHead className="text-xs">Erreurs</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-sm text-muted-foreground">Chargement...</TableCell></TableRow>
                ) : jobs.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-sm text-muted-foreground">Aucun job</TableCell></TableRow>
                ) : jobs.map((j, i) => (
                  <TableRow key={j.id || i} data-testid={`job-row-${j.id || i}`}>
                    <TableCell><TypeBadge type={j.type} /></TableCell>
                    <TableCell className="text-xs">{j.tenant_name || '-'}</TableCell>
                    <TableCell><StatusBadge status={j.status} /></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{formatDate(j.started_at)}</TableCell>
                    <TableCell className="text-xs font-mono">{duration(j.started_at, j.finished_at)}</TableCell>
                    <TableCell className="text-center text-sm">{j.items_processed ?? 0}</TableCell>
                    <TableCell className="text-center text-sm">{j.items_created ?? 0}</TableCell>
                    <TableCell className="text-xs text-destructive max-w-[200px] truncate">
                      {j.errors?.length > 0 ? j.errors[0] : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Pagination */}
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">{total} job(s) au total</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="jobs-prev-page">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="flex items-center text-sm text-muted-foreground px-2">Page {page + 1}</span>
            <Button variant="outline" size="sm" disabled={jobs.length < PAGE_SIZE} onClick={() => setPage(p => p + 1)} data-testid="jobs-next-page">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </PlatformLayout>
  );
}
